import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import styled from "styled-components";
import gsap from "gsap";
import { colors, pages } from "@util/constants";

/** Fired when the opening mask timeline finishes (or immediately if reduced motion / skipped). */
export const PAGE_REVEAL_COMPLETE_EVENT = "zuru:pageRevealComplete";
/** Fired when a new entrance sequence starts so UI (e.g. hero tagline) can reset before completion. */
export const PAGE_REVEAL_RESET_EVENT = "zuru:pageRevealReset";

const SESSION_KEY = "zuru_entrance_reveal_shown";

/** Must match the boot script in `gatsby/gatsby-ssr.js` (no white flash before hydration). */
const REDUCED_MOTION_MQ = "(prefers-reduced-motion: reduce)";

/** Inline boot veil (see gatsby-ssr); removed once the React overlay paints or reveal is skipped. */
export const PAGE_REVEAL_BOOT_VEIL_ID = "zuru-page-reveal-boot-veil";

export function removePageRevealBootVeil(): void {
  if (typeof document === "undefined") return;
  document.getElementById(PAGE_REVEAL_BOOT_VEIL_ID)?.remove();
}

let pageRevealAnimationFinished = false;

export function isPageRevealAnimationFinished(): boolean {
  return pageRevealAnimationFinished;
}

function pathForReveal(routePath: string | null | undefined): string {
  if (typeof window === "undefined") return (routePath ?? "").trim();
  return (routePath ?? window.location.pathname).trim();
}

function isHomeRoute(path: string): boolean {
  const s = path.split("?")[0]?.trim() ?? "";
  if (s === "" || s === "/") return true;
  const norm = s.startsWith("/") ? s : `/${s}`;
  return norm === pages.home;
}

/** First visit in the tab shows the mask; later navigations only replay on home. */
export function shouldPlayEntranceReveal(routePath: string | null | undefined): boolean {
  if (typeof window === "undefined") return false;
  try {
    const p = pathForReveal(routePath);
    const seen = sessionStorage.getItem(SESSION_KEY);
    if (!seen) return true;
    return isHomeRoute(p);
  } catch {
    return true;
  }
}

function notifyPageRevealStarted(): void {
  pageRevealAnimationFinished = false;
  if (typeof window === "undefined") return;
  window.dispatchEvent(new CustomEvent(PAGE_REVEAL_RESET_EVENT));
}

function notifyPageRevealComplete(): void {
  pageRevealAnimationFinished = true;
  if (typeof window === "undefined") return;
  window.dispatchEvent(new CustomEvent(PAGE_REVEAL_COMPLETE_EVENT));
}

let pageRevealMaskSeq = 0;

/** Collapsed hole (`s === 0`) blooms out to the small hold shape (`s0`). */
const BLOOM_S = 0.36;
const HOLD_S = 0.5;
const GROW_S = 1.1;
const FADE_S = 0.32;
const FADE_OVERLAP = 0.27;

/** Start veil fade a bit after the hole grows so the opening leads, then site appears with the growth. */
const VEIL_FADE_DELAY = 0.35;
/** Fade spans from there through the end of the grow (plus a short tail) so it tracks the shape. */
const VEIL_FADE_S = GROW_S - VEIL_FADE_DELAY + 0.13;

/** Veil starts fully opaque so the site is hidden; fades to 0 as the hole opens (no layout opacity). */
const VEIL_START_OPACITY = 1;

/** Design hole in design units 0–100 (same aspect in a square reference frame). */
const HOLE_VERTS: ReadonlyArray<[number, number]> = [
  [99, 51],
  [95, 100],
  [8, 100],
  [0, 61],
];

const HOLE_CX_P =
  HOLE_VERTS.reduce((a, [vx]) => a + vx, 0) / HOLE_VERTS.length;
const HOLE_CY_P =
  HOLE_VERTS.reduce((a, [, vy]) => a + vy, 0) / HOLE_VERTS.length;

/** Map design (0–100)² into a centered square of side `min(w,h)` — one scale for x and y (fixed aspect ratio). */
function designVertexToPixel(
  vx: number,
  vy: number,
  w: number,
  h: number
): { x: number; y: number } {
  const L = Math.min(w, h);
  const marginX = (w - L) / 2;
  const marginY = (h - L) / 2;
  return {
    x: marginX + (vx / 100) * L,
    y: marginY + (vy / 100) * L,
  };
}

/** Mask polygon in pixels (`userSpaceOnUse`). Grows from viewport center with uniform scale. */
function holeMaskPolygonPoints(s: number, w: number, h: number): string {
  const O = { x: w / 2, y: h / 2 };
  const C = designVertexToPixel(HOLE_CX_P, HOLE_CY_P, w, h);

  return HOLE_VERTS.map(([vx, vy]) => {
    const V = designVertexToPixel(vx, vy, w, h);
    const x = O.x + s * (V.x - C.x);
    const y = O.y + s * (V.y - C.y);
    return `${x.toFixed(2)},${y.toFixed(2)}`;
  }).join(" ");
}

/** Max `s` for grow tween; tall portrait needs more than landscape so the hole fully clears the mask. */
const GROW_S_MAX_BASE = 2.95;

function computeMaxGrowScale(w: number, h: number): number {
  const portrait = h / w;
  let s = GROW_S_MAX_BASE;
  if (portrait > 1.52) {
    s += (portrait - 1.52) * 2.78;
  }
  if (portrait > 1.95) {
    s += 1.05;
  }
  if (portrait > 2.12) {
    s += 0.55;
  }
  return Math.min(s, 15);
}

/** Starting `s` during hold; slightly larger on portrait so the opening isn’t pin‑tiny on phones. */
const GROW_S_MIN_BASE = 0.07;

function computeInitialGrowScale(w: number, h: number): number {
  const portrait = h / w;
  if (portrait <= 1.52) return GROW_S_MIN_BASE;
  return Math.min(0.15, GROW_S_MIN_BASE + (portrait - 1.52) * 0.14);
}

const Veil = styled.div`
  position: fixed;
  inset: 0;
  z-index: 2147482999;
  pointer-events: none;
  background: ${colors.white};
  will-change: opacity;
`;

interface PageRevealOverlayProps {
  /** Sanity slug or path segment; falls back to `location.pathname` when omitted. */
  routePath?: string | null;
}

export default function PageRevealOverlay({ routePath }: PageRevealOverlayProps) {
  const [maskId] = useState(() => `page-reveal-mask-${++pageRevealMaskSeq}`);
  const [mounted, setMounted] = useState(false);
  const [animKey, setAnimKey] = useState(0);
  const polyRef = useRef<SVGPolygonElement>(null);
  const maskRef = useRef<SVGMaskElement>(null);
  const maskRectRef = useRef<SVGRectElement>(null);
  const veilRef = useRef<HTMLDivElement>(null);
  const svgRootRef = useRef<SVGSVGElement>(null);
  const overlayRectRef = useRef<SVGRectElement>(null);
  const [done, setDone] = useState(false);

  useLayoutEffect(() => {
    if (typeof window === "undefined") return;

    const play = shouldPlayEntranceReveal(routePath);

    if (window.matchMedia(REDUCED_MOTION_MQ).matches) {
      removePageRevealBootVeil();
      if (play) {
        try {
          sessionStorage.setItem(SESSION_KEY, "1");
        } catch {
          /* ignore */
        }
      }
      /* No overlay, no reset event — entrance is omitted entirely for vestibular safety. */
      notifyPageRevealComplete();
      setDone(true);
      return;
    }

    if (!play) {
      removePageRevealBootVeil();
      notifyPageRevealComplete();
      setDone(true);
      return;
    }

    notifyPageRevealStarted();
    setDone(false);
    setMounted(true);
    setAnimKey(k => k + 1);
  }, [routePath]);

  useLayoutEffect(() => {
    if (!mounted) return;

    removePageRevealBootVeil();

    const svgRoot = svgRootRef.current;
    const overlayRect = overlayRectRef.current;
    const poly = polyRef.current;
    const veil = veilRef.current;
    const maskEl = maskRef.current;
    const maskRectEl = maskRectRef.current;
    if (!svgRoot || !overlayRect || !poly || !veil || !maskEl || !maskRectEl) {
      /* Avoid leaving the white veil + overlay mounted with no running timeline (blank screen). */
      removePageRevealBootVeil();
      notifyPageRevealComplete();
      setMounted(false);
      setDone(true);
      return;
    }

    const w0 = window.innerWidth;
    const h0 = window.innerHeight;
    const s0 = computeInitialGrowScale(w0, h0);
    const state = { s: 0, opacity: 1, veil: VEIL_START_OPACITY };

    const syncMaskViewport = () => {
      const w = window.innerWidth;
      const h = window.innerHeight;
      maskEl.setAttribute("width", String(w));
      maskEl.setAttribute("height", String(h));
      maskRectEl.setAttribute("width", String(w));
      maskRectEl.setAttribute("height", String(h));
      svgRoot.setAttribute("width", String(w));
      svgRoot.setAttribute("height", String(h));
      svgRoot.setAttribute("viewBox", `0 0 ${w} ${h}`);
    };

    const paint = () => {
      /* `ctx.revert()` on teardown can reset the tweened values; skip frames without a scale. */
      if (!Number.isFinite(state.s)) return;
      try {
        const w = window.innerWidth;
        const h = window.innerHeight;
        syncMaskViewport();
        poly.setAttribute("points", holeMaskPolygonPoints(state.s, w, h));
        overlayRect.style.opacity = String(state.opacity);
        veil.style.opacity = String(state.veil);
      } catch {
        /* GSAP onUpdate can run after teardown in Strict Mode; ignore. */
      }
    };

    const onResize = () => {
      paint();
    };

    paint();
    window.addEventListener("resize", onResize);

    const ctx = gsap.context(() => {
      const tl = gsap.timeline({
        onComplete: () => {
          try {
            sessionStorage.setItem(SESSION_KEY, "1");
          } catch {
            /* ignore */
          }
          notifyPageRevealComplete();
          setDone(true);
        },
      });

      const sMax = computeMaxGrowScale(w0, h0);

      tl.fromTo(
        state,
        { s: 0 },
        {
          s: s0,
          duration: BLOOM_S,
          ease: "power2.out",
          onUpdate: paint,
        }
      );

      const holdTick = { _: 0 };
      tl.to(holdTick, { _: 1, duration: HOLD_S, ease: "none" });
      tl.to(state, {
        s: sMax,
        duration: GROW_S,
        ease: "power3.in",
        onUpdate: paint,
      });
      tl.to(
        state,
        {
          veil: 0,
          duration: VEIL_FADE_S,
          ease: "power2.out",
          onUpdate: paint,
        },
        `<+=${VEIL_FADE_DELAY}`
      );
      tl.to(
        state,
        {
          opacity: 0,
          duration: FADE_S,
          ease: "power2.inOut",
          onUpdate: paint,
        },
        `-=${FADE_OVERLAP}`
      );
    }, svgRoot);

    return () => {
      window.removeEventListener("resize", onResize);
      ctx.revert();
    };
  }, [mounted, animKey]);

  /** If the timeline never completes (GSAP / ref edge case), do not leave the site covered. */
  useEffect(() => {
    if (!mounted || done) return undefined;
    const maxMs = 8000;
    const t = window.setTimeout(() => {
      removePageRevealBootVeil();
      try {
        sessionStorage.setItem(SESSION_KEY, "1");
      } catch {
        /* ignore */
      }
      notifyPageRevealComplete();
      setMounted(false);
      setDone(true);
    }, maxMs);
    return () => window.clearTimeout(t);
  }, [mounted, done]);

  if (done) return null;
  if (!mounted) return null;

  const w = window.innerWidth;
  const h = window.innerHeight;

  return (
    <>
      <Veil ref={veilRef} aria-hidden style={{ opacity: VEIL_START_OPACITY }} />
      {/*
        Safari/iOS does not reliably apply CSS mask-image: url(#id) to HTML elements
        for inline SVG masks. Apply the same mask to an SVG <rect> instead.
      */}
      <svg
        ref={svgRootRef}
        aria-hidden
        width={w}
        height={h}
        viewBox={`0 0 ${w} ${h}`}
        preserveAspectRatio="none"
        style={{
          position: "fixed",
          left: 0,
          top: 0,
          width: "100%",
          height: "100%",
          zIndex: 2147483000,
          pointerEvents: "auto",
        }}
      >
        <defs>
          <mask
            ref={maskRef}
            id={maskId}
            maskUnits="userSpaceOnUse"
            maskContentUnits="userSpaceOnUse"
            x="0"
            y="0"
            width={w}
            height={h}
          >
            <rect ref={maskRectRef} x="0" y="0" width={w} height={h} fill="white" />
            <polygon ref={polyRef} fill="black" points={holeMaskPolygonPoints(0, w, h)} />
          </mask>
        </defs>
        <rect
          ref={overlayRectRef}
          x="0"
          y="0"
          width="100%"
          height="100%"
          fill={colors.red}
          mask={`url(#${maskId})`}
          style={{ opacity: 1 }}
        />
      </svg>
    </>
  );
}
